import React, { useLayoutEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { colors } from "style";
import { initializeAmCharts } from "utils";
import { Spinner } from "components/Loader";
import { MessagesError, MessagesNoResults } from "components/Messages";

const formatHoursAndMinutes = (hours) => {
  if (!hours) return "0h";

  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  if (minutes === 0) return `${wholeHours}h`;

  return `${wholeHours}h ${minutes}m`;
};

/**
 * VerticalBar
 *
 * Generic vertical bar chart component
 *
 * @param {Object} data Chart data and configuration
 * @param {String} height Chart height
 * @param {Boolean} loading Loading state
 * @param {String} error Error message
 * @param {Object} noResultConfigs No result configurations
 */
const VerticalBar = ({ data, loading, error, height, noResultConfigs, ...props }) => {
  const [rootId] = useState(`vertical-bar-${Math.random()}`);

  useLayoutEffect(() => {
    if (!data?.data || loading) return;

    initializeAmCharts();

    let root = am5.Root.new(rootId);

    // Create and configure theme
    let customTheme = am5.Theme.new(root);

    customTheme.rule("AxisLabel", ["minor"]).setAll({
      fontSize: 11,
    });

    customTheme.rule("Label").setAll({
      fontFamily: "Matter",
      fill: am5.color(colors.grayAnatomyBase),
      fontSize: 11,
    });

    customTheme.rule("Grid").setAll({
      strokeDasharray: [3],
    });

    // Disable logo in theme
    customTheme.rule("Container").setAll({
      logo: {
        disabled: true,
      },
    });

    root.setThemes([am5themes_Animated.new(root), customTheme]);

    // Create chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        wheelX: "none",
        wheelY: "none",
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingTop: 0,
        paddingBottom: 0,
        paddingRight: 10,
        paddingLeft: 10,
      })
    );

    chart.zoomOutButton.set("forceHidden", true);

    // Create X axis
    var xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 1,
      cellStartLocation: 0.2,
      cellEndLocation: 0.8,
    });

    xRenderer.grid.template.set("visible", false);

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "day",
        renderer: xRenderer,
      })
    );

    xAxis.data.setAll(data.data);

    // Create Y axis
    var yRenderer = am5xy.AxisRendererY.new(root, {
      strokeOpacity: 0,
      minGridDistance: 30,
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: yRenderer,
        numberFormat: "#'h'",
      })
    );

    // Create series
    let series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: data.seriesName,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "hours",
        categoryXField: "day",
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: "horizontal",
          getFillFromSprite: false,
          labelText: "",
          autoTextColor: false,
        }),
      })
    );

    // Configure tooltip
    const tooltip = series.get("tooltip");
    tooltip.get("background").setAll({
      fill: am5.color(colors.grayAnatomyBase),
      fillOpacity: 1,
      stroke: am5.color(colors.grayAnatomyBase),
      strokeOpacity: 1,
      cornerRadius: 4,
    });

    tooltip.label.setAll({
      fill: am5.color(colors.white),
      fontSize: "1.2rem",
      fontFamily: "Matter",
    });

    // Add tooltip text adapter
    tooltip.adapters.add("labelText", (text, target) => {
      const hours = target.dataItem?.get("valueY") || 0;

      return formatHoursAndMinutes(hours);
    });

    // Style columns
    const barColor = am5.color(data.barColor);
    series.columns.template.setAll({
      fillOpacity: 1.0,
      strokeWidth: 0,
      strokeOpacity: 0,
      cornerRadiusTopLeft: 4,
      cornerRadiusTopRight: 4,
      fill: barColor,
      stroke: barColor,
      tooltipY: 0,
    });

    series.data.setAll(data.data);

    // Add cursor
    let cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "none",
      })
    );

    cursor.lineY.set("forceHidden", true);
    cursor.lineX.set("forceHidden", true);

    series.appear(1000, 100);
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [data, loading, rootId]);

  return (
    <ChartContainer>
      <ChartCanvas id={rootId} minHeight={height} shouldCenter={loading || (!loading && !data?.data)} {...props}>
        {loading ? (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        ) : !data?.data && !error ? (
          <MessagesNoResults {...noResultConfigs} />
        ) : error ? (
          <MessagesError />
        ) : null}
      </ChartCanvas>
    </ChartContainer>
  );
};

const ChartContainer = styled.div`
  width: 100%;
  padding: 0;
  overflow: visible;
`;

const SpinnerContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -1.5rem;
  margin-left: -1.5rem;
`;

const ChartCanvas = styled.div`
  width: 100%;
  min-height: ${(props) => props.minHeight};
  overflow: visible;
  ${(props) =>
    props.shouldCenter &&
    `
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;

VerticalBar.defaultProps = {
  height: "27rem",
};

VerticalBar.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    seriesName: PropTypes.string,
    barColor: PropTypes.string,
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  height: PropTypes.string,
  noResultConfigs: PropTypes.object,
};

export default VerticalBar;
